
import React, { useState } from 'react';
import { Website, PromoBar, Popup, GamificationGame, GameOption } from '../types';
import { Icons } from '../constants';

interface MarketingProps {
  websites: Website[];
  onUpdateSite: (site: Website) => void;
}

export const Marketing: React.FC<MarketingProps> = ({ websites, onUpdateSite }) => {
  const [selectedSiteId, setSelectedSiteId] = useState(websites[0]?.id || '');
  const [activeTab, setActiveTab] = useState<'campaigns' | 'gamification'>('campaigns');
  
  const selectedSite = websites.find(s => s.id === selectedSiteId);

  if (!selectedSite) return <div className="p-12 text-center text-slate-400">No websites found. Please create one first.</div>;

  const generateId = (prefix: string) => {
    return `${prefix}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
  };

  // Promo Bar Logic
  const addPromoBar = () => {
    const newBar: PromoBar = {
      id: generateId('BAR'),
      isActive: true,
      text: '🎉 Exclusive Offer: Get 20% off your first order!',
      buttonText: 'Claim Now',
      link: '#',
      color: '#6366f1'
    };
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, promoBars: [...(selectedSite.marketing.promoBars || []), newBar] }
    });
  };

  const updatePromoBar = (id: string, updates: Partial<PromoBar>) => {
    const updatedBars = (selectedSite.marketing.promoBars || []).map(b => b.id === id ? { ...b, ...updates } : b);
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, promoBars: updatedBars }
    });
  };

  const removePromoBar = (id: string) => {
    if (!window.confirm("Delete this banner?")) return;
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, promoBars: selectedSite.marketing.promoBars.filter(b => b.id !== id) }
    });
  };

  // Popup / Flyer Logic
  const addPopup = () => {
    const newPopup: Popup = {
      id: generateId('FLYER'),
      isActive: true,
      title: 'Limited Time Flash Sale!',
      description: 'Subscribe now and get an extra 10% discount on your next purchase. Valid for the next 24 hours only!',
      imageUrl: 'https://picsum.photos/600/400?random=' + Math.random(),
      activeCampaign: 'Flash Sale'
    };
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, popups: [...(selectedSite.marketing.popups || []), newPopup] }
    });
  };

  const updatePopup = (id: string, updates: Partial<Popup>) => {
    const updatedPopups = (selectedSite.marketing.popups || []).map(p => p.id === id ? { ...p, ...updates } : p);
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, popups: updatedPopups }
    });
  };

  const removePopup = (id: string) => {
    if (!window.confirm("Delete this flyer?")) return;
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, popups: selectedSite.marketing.popups.filter(p => p.id !== id) }
    });
  };

  // Gamification Logic
  const addGame = (type: GamificationGame['type']) => {
    const defaultOptions: GameOption[] = [
      { label: '10% OFF', value: 'DISC10', probability: 40, color: '#6366f1' },
      { label: 'FREE GIFT', value: 'GIFT', probability: 10, color: '#8b5cf6' },
      { label: 'TRY AGAIN', value: 'NONE', probability: 30, color: '#cbd5e1' },
      { label: '50% OFF', value: 'JACKPOT', probability: 2, color: '#f43f5e' },
    ];

    const newGame: GamificationGame = {
      id: generateId('GAME'),
      type,
      isActive: true,
      title: `Active ${type === 'wheel' ? 'Spin to Win' : type === 'scratch' ? 'Mystery Scratch' : type === 'slots' ? 'Lucky Slots' : 'Treasure Hunt'}`,
      options: defaultOptions,
      expiryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      requirePhone: false
    };

    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, games: [...(selectedSite.marketing.games || []), newGame] }
    });
  };

  const updateGame = (id: string, updates: Partial<GamificationGame>) => {
    const updatedGames = (selectedSite.marketing.games || []).map(g => g.id === id ? { ...g, ...updates } : g);
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, games: updatedGames }
    });
  };

  const removeGame = (id: string) => {
    if (!window.confirm("Remove this game?")) return;
    onUpdateSite({
      ...selectedSite,
      marketing: { ...selectedSite.marketing, games: (selectedSite.marketing.games || []).filter(g => g.id !== id) }
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-10 pb-24">
      {/* Site Selector & Tabs */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Marketing Growth Lab</h2>
            <p className="text-slate-500 text-sm font-medium">Manage conversion bars, popups, and games.</p>
          </div>
          <select 
            className="bg-slate-50 border border-slate-200 rounded-2xl px-6 py-3.5 outline-none font-bold text-slate-700 min-w-[240px] shadow-inner"
            value={selectedSiteId}
            onChange={(e) => setSelectedSiteId(e.target.value)}
          >
            {websites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="flex bg-slate-100 p-1.5 rounded-2xl w-fit">
          <button 
            onClick={() => setActiveTab('campaigns')}
            className={`px-8 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'campaigns' ? 'bg-white shadow-md text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Traditional Campaigns
          </button>
          <button 
            onClick={() => setActiveTab('gamification')}
            className={`px-8 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'gamification' ? 'bg-white shadow-md text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Icons.Sparkles className="w-4 h-4" /> Gamification Center
          </button>
        </div>
      </div>

      {activeTab === 'campaigns' ? (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-16">
          {/* Header Notification Bars Section */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <Icons.Megaphone />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Header Notification Bars</h3>
                  <p className="text-xs text-slate-400">Fixed promotional bars at the top of your website.</p>
                </div>
              </div>
              <button onClick={addPromoBar} className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100">
                <Icons.Plus /> New Banner
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {(selectedSite.marketing.promoBars || []).map((bar) => (
                <div key={bar.id} className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm flex flex-col group hover:border-indigo-400 transition-all">
                  <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: bar.color }}>
                        {bar.isActive ? 'ON' : 'OFF'}
                      </div>
                      <span className="text-[10px] font-mono text-slate-400">ID: {bar.id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                       <button 
                         onClick={() => updatePromoBar(bar.id, { isActive: !bar.isActive })}
                         className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${
                           bar.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                         }`}
                       >
                         {bar.isActive ? 'Active' : 'Draft'}
                       </button>
                       <button onClick={() => removePromoBar(bar.id)} className="p-2 text-slate-300 hover:text-rose-500 transition-colors"><Icons.Trash /></button>
                    </div>
                  </div>

                  <div className="p-8 space-y-5">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Banner Message</label>
                      <input 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-2 ring-indigo-500/20" 
                        value={bar.text} 
                        onChange={(e) => updatePromoBar(bar.id, { text: e.target.value })} 
                        placeholder="e.g. 50% discount ends tonight!"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Button Text</label>
                        <input 
                          className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-sm font-bold outline-none" 
                          value={bar.buttonText || ''} 
                          onChange={(e) => updatePromoBar(bar.id, { buttonText: e.target.value })} 
                          placeholder="Sign Up Now"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Link (URL)</label>
                        <input 
                          className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-sm font-bold outline-none" 
                          value={bar.link} 
                          onChange={(e) => updatePromoBar(bar.id, { link: e.target.value })} 
                          placeholder="/register"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Theme Color</label>
                      <div className="flex items-center gap-3">
                        <input 
                          type="color" 
                          className="w-12 h-12 p-1 bg-white border border-slate-100 rounded-xl cursor-pointer" 
                          value={bar.color} 
                          onChange={(e) => updatePromoBar(bar.id, { color: e.target.value })} 
                        />
                        <span className="text-xs font-mono text-slate-400 uppercase">{bar.color}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-auto p-4 bg-slate-50 border-t border-slate-100">
                    <div className="w-full py-2 px-4 rounded-lg flex items-center justify-between text-white" style={{ backgroundColor: bar.color }}>
                      <span className="text-[10px] font-bold truncate max-w-[70%]">{bar.text}</span>
                      {bar.buttonText && <span className="bg-white/20 px-2 py-0.5 rounded text-[8px] font-black uppercase whitespace-nowrap">{bar.buttonText}</span>}
                    </div>
                  </div>
                </div>
              ))}
              {(selectedSite.marketing.promoBars || []).length === 0 && (
                <div className="md:col-span-2 py-20 border-2 border-dashed border-slate-200 rounded-[3rem] flex flex-col items-center justify-center text-slate-400 bg-white/50">
                  <Icons.Megaphone className="w-12 h-12 mb-4 opacity-20" />
                  <p className="font-bold">No promo banners created yet.</p>
                  <button onClick={addPromoBar} className="mt-4 text-indigo-600 font-bold text-sm hover:underline">Create your first banner</button>
                </div>
              )}
            </div>
          </section>

          {/* Popups / Flyers Section */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                  <Icons.Dashboard />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Marketing Flyers (Popups)</h3>
                  <p className="text-xs text-slate-400">High-impact overlay flyers for announcements or lead capture.</p>
                </div>
              </div>
              <button onClick={addPopup} className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-100">
                <Icons.Plus /> New Flyer
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {(selectedSite.marketing.popups || []).map((popup) => (
                <div key={popup.id} className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm flex flex-col group hover:border-indigo-400 transition-all">
                  <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${popup.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                      <span className="text-[10px] font-mono text-slate-400">ID: {popup.id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                       <button 
                         onClick={() => updatePopup(popup.id, { isActive: !popup.isActive })}
                         className={`px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${
                           popup.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                         }`}
                       >
                         {popup.isActive ? 'Active' : 'Disabled'}
                       </button>
                       <button onClick={() => removePopup(popup.id)} className="p-2 text-slate-300 hover:text-rose-500 transition-colors"><Icons.Trash /></button>
                    </div>
                  </div>

                  <div className="p-8 space-y-5">
                    <div className="flex gap-4">
                      <div className="w-24 h-24 bg-slate-100 rounded-2xl overflow-hidden shrink-0 border border-slate-100">
                        <img src={popup.imageUrl} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Flyer Title</label>
                        <input 
                          className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-sm font-bold outline-none" 
                          value={popup.title} 
                          onChange={(e) => updatePopup(popup.id, { title: e.target.value })} 
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Offer Description</label>
                      <textarea 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-medium outline-none min-h-[80px]" 
                        value={popup.description} 
                        onChange={(e) => updatePopup(popup.id, { description: e.target.value })} 
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Campaign Tag</label>
                      <input 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-xs font-bold outline-none" 
                        value={popup.activeCampaign} 
                        onChange={(e) => updatePopup(popup.id, { activeCampaign: e.target.value })} 
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Image URL</label>
                      <input 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2 text-[10px] font-mono outline-none" 
                        value={popup.imageUrl} 
                        onChange={(e) => updatePopup(popup.id, { imageUrl: e.target.value })} 
                      />
                    </div>
                  </div>
                </div>
              ))}
              {(selectedSite.marketing.popups || []).length === 0 && (
                <div className="md:col-span-2 py-20 border-2 border-dashed border-slate-200 rounded-[3rem] flex flex-col items-center justify-center text-slate-400 bg-white/50">
                  <Icons.Dashboard className="w-12 h-12 mb-4 opacity-20" />
                  <p className="font-bold">No marketing flyers created yet.</p>
                  <button onClick={addPopup} className="mt-4 text-indigo-600 font-bold text-sm hover:underline">Create your first flyer</button>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-12">
          {/* Gamification Template Suggestions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { type: 'wheel', name: 'Spin to Win', icon: '🎡', desc: 'Classic engagement.' },
              { type: 'scratch', name: 'Mystery Scratch', icon: '💎', desc: 'Curiosity driven.' },
              { type: 'slots', name: 'Lucky Slots', icon: '🎰', desc: 'Match symbols.' },
              { type: 'chest', name: 'Treasure Hunt', icon: '🎁', desc: 'Pick and win.' }
            ].map((suggest) => (
              <button 
                key={suggest.type}
                onClick={() => addGame(suggest.type as GamificationGame['type'])}
                className="bg-white p-6 rounded-[2.5rem] border border-slate-100 hover:border-indigo-500 text-left transition-all hover:shadow-xl group"
              >
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">{suggest.icon}</div>
                <h4 className="font-bold text-slate-900 mb-1">{suggest.name}</h4>
                <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs">
                   Add <Icons.Plus className="w-3 h-3" />
                </div>
              </button>
            ))}
          </div>

          <div className="h-px bg-slate-200"></div>

          {/* Active Games Management */}
          <section className="space-y-8">
            <h3 className="text-xl font-bold">Active Games</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {(selectedSite.marketing.games || []).map((game) => (
                <div key={game.id} className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm flex flex-col">
                  <div className="p-6 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                       <div className="w-10 h-10 rounded-2xl bg-indigo-600 flex items-center justify-center text-white font-bold text-xl">
                          {game.type === 'wheel' ? '🎡' : game.type === 'scratch' ? '💎' : game.type === 'slots' ? '🎰' : '🎁'}
                       </div>
                       <h4 className="font-bold text-sm">{game.title}</h4>
                    </div>
                    <button onClick={() => removeGame(game.id)} className="p-2 text-slate-400 hover:text-rose-500 transition-colors"><Icons.Trash /></button>
                  </div>
                  <div className="p-8">
                    <div className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl">
                       <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Game Status</span>
                       <button 
                         onClick={() => updateGame(game.id, { isActive: !game.isActive })}
                         className={`px-6 py-2 rounded-xl text-xs font-bold transition-all ${
                           game.isActive ? 'bg-emerald-500 text-white shadow-lg' : 'bg-slate-200 text-slate-600'
                         }`}
                       >
                         {game.isActive ? 'Active & Live' : 'Disabled'}
                       </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};
